import React, { useState, useEffect } from 'react';
import { supabase, Nomination, JuryVote, Category } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import JuryVotingForm from '../voting/JuryVotingForm';
import NominationDetails from '../nominations/NominationDetails';
import './Dashboard.css';

const JuryDashboard: React.FC = () => {
  const [myCategories, setMyCategories] = useState<Category[]>([]);
  const [nominations, setNominations] = useState<Nomination[]>([]);
  const [myVotes, setMyVotes] = useState<JuryVote[]>([]);
  const [selectedNomination, setSelectedNomination] = useState<Nomination | null>(null);
  const [loading, setLoading] = useState(true);
  const [platformConfig, setPlatformConfig] = useState<any>(null);

  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadJuryData();
      loadPlatformConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadPlatformConfig = async () => {
    try {
      const { data, error } = await supabase
        .schema('premios')
        .from('platform_config')
        .select('*')
        .single();

      if (error) throw error;
      setPlatformConfig(data);
    } catch (error) {
      console.error('Error loading platform config:', error);
    }
  };

  const loadJuryData = async () => {
    try {
      setLoading(true);

      // Cargar las categorías asignadas
      const { data: juryMemberships, error: membershipError } = await supabase
        .schema('premios')
        .from('jury_members')
        .select('category_id, categories(*)')
        .eq('user_id', user?.id);

      if (membershipError) throw membershipError;

      const categories = juryMemberships?.map((jm: any) => jm.categories) || [];
      setMyCategories(categories);

      // Cargar candidaturas aprobadas de mis categorías
      if (categories.length > 0) {
        const categoryIds = categories.map((c: any) => c.id);
        const { data: noms, error: nomsError } = await supabase
          .schema('premios')
          .from('nominations')
          .select('*')
          .in('category_id', categoryIds)
          .eq('status', 'approved')
          .order('created_at', { ascending: false });

        if (nomsError) throw nomsError;
        setNominations(noms || []);
      }

      // Cargar mis votos (para saber qué candidaturas ya he evaluado)
      const { data: votes, error: votesError } = await supabase
        .schema('premios')
        .from('jury_votes')
        .select('*')
        .eq('jury_member_id', user?.id);

      if (votesError) throw votesError;
      setMyVotes(votes || []);
    } catch (error) {
      console.error('Error loading jury data:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasVoted = (nominationId: string) => {
    return myVotes.some((v) => v.nomination_id === nominationId);
  };

  const getVote = (nominationId: string) => {
    return myVotes.find((v) => v.nomination_id === nominationId);
  };

  const getCategoryById = (id: string): Category | undefined => {
    return myCategories.find((c) => c.id === id);
  };

  const handleVoteSubmit = () => {
    loadJuryData();
    setSelectedNomination(null);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const votingDisabled = !platformConfig?.jury_voting_enabled;

  if (myCategories.length === 0) {
    return (
      <div className="container dashboard">
        <div className="empty-state">
          <h3>No tienes categorías asignadas</h3>
          <p>Contacta con el equipo de NovaGob para que te asignen categorías.</p>
        </div>
      </div>
    );
  }

  const votedCount = myVotes.length;
  const totalCount = nominations.length;

  return (
    <div className="container dashboard">
      <div className="dashboard-header">
        <h1>Panel del Jurado</h1>
        <p>Evaluación de candidaturas</p>
      </div>

      {votingDisabled && (
        <div
          style={{
            marginTop: 16,
            marginBottom: 24,
            padding: '14px 16px',
            borderRadius: 8,
            background: 'rgba(234, 179, 8, 0.1)',
            border: '1px solid rgba(234, 179, 8, 0.4)',
            color: '#fde68a',
            fontSize: '0.95rem',
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: '#fcd34d', display: 'block', marginBottom: 4 }}>
            Votación del jurado no disponible
          </strong>
          En este momento no puedes evaluar candidaturas. Puedes seguir consultando las candidaturas
          asignadas y tus evaluaciones previas en modo solo lectura.
        </div>
      )}

      {/* Estadísticas */}
      <div className="grid grid-2">
        <div className="stat-card">
          <div className="stat-value">{votedCount}</div>
          <div className="stat-label">Candidaturas evaluadas</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{totalCount - votedCount}</div>
          <div className="stat-label">Pendientes de evaluar</div>
        </div>
      </div>

      {/* Mis categorías */}
      <div className="card" style={{ marginTop: '24px' }}>
        <h2>Mis Categorías Asignadas</h2>
        <div className="grid grid-3" style={{ marginTop: '16px' }}>
          {myCategories.map((cat) => (
            <div key={cat.id} className="card">
              <h3>{cat.name}</h3>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>
                {cat.type === 'personal' ? 'Categoría Personal' : 'Categoría de Proyecto'}
              </p>
              <p style={{ marginTop: '8px' }}>
                {nominations.filter((n) => n.category_id === cat.id).length} candidaturas
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Candidaturas por categoría */}
      {myCategories.map((category) => {
        const categoryNominations = nominations.filter(
          (n) => n.category_id === category.id
        );

        return (
          <div key={category.id} className="card" style={{ marginTop: '24px' }}>
            <div className="category-header">
              <h2>{category.name}</h2>
              <span className="badge badge-approved">
                {categoryNominations.length} candidaturas
              </span>
            </div>
            {categoryNominations.length > 0 && (
              <table className="table table-responsive">
                <thead>
                  <tr>
                    <th>Candidat@/Proyecto</th>
                    <th>Estado de Votación</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryNominations.map((nom) => {
                    const voted = hasVoted(nom.id);
                    return (
                      <tr key={nom.id}>
                        <td data-label="Candidat@/Proyecto">
                          <div>
                            <div>{nom.candidate_name || nom.project_title}</div>
                            {nom.institution && (
                              <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                                {nom.institution}
                              </div>
                            )}
                          </div>
                        </td>
                        <td data-label="Estado">
                          {voted ? (
                            <span className="vote-status">✓ Evaluada</span>
                          ) : (
                            <span className="badge badge-warning">Pendiente</span>
                          )}
                        </td>
                        <td data-label="Acciones">
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => setSelectedNomination(nom)}
                          >
                            {votingDisabled
                              ? 'Ver detalles'
                              : voted
                              ? 'Ver/Editar evaluación'
                              : 'Evaluar'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        );
      })}

      {nominations.length === 0 && (
        <div className="card" style={{ marginTop: '24px' }}>
          <div className="empty-state">
            <h3>No hay candidaturas aprobadas aún</h3>
            <p>Las candidaturas aparecerán aquí una vez sean aprobadas por el equipo de NovaGob.</p>
          </div>
        </div>
      )}

      {/* Modal de votación */}
      {selectedNomination && (
        <div className="modal-overlay" onClick={() => setSelectedNomination(null)}>
          <div
            className="modal modal-wide"
            onClick={(e) => e.stopPropagation()}
          >
            {votingDisabled ? (
              <NominationDetails
                nomination={selectedNomination}
                category={getCategoryById(selectedNomination.category_id)}
                onClose={() => setSelectedNomination(null)}
                showActions={false}
              />
            ) : (
              <JuryVotingForm
                nomination={selectedNomination}
                category={getCategoryById(selectedNomination.category_id)}
                existingVote={getVote(selectedNomination.id)}
                onClose={() => setSelectedNomination(null)}
                onSubmit={handleVoteSubmit}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default JuryDashboard;
